import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import GitHubStats from '@/components/GitHubStats';
import GitHubContributions from '@/components/GitHubContributions';
import CompanyList from '@/components/CompanyList';
import { ArrowRight, Calendar, GraduationCap } from 'lucide-react';
import { getAllPosts, BlogPost as BlogPostType } from '../utils/markdown';

const Index = () => {
  const handleBookingRedirect = () => {
    const calendarUrl = 'https://calendly.com/ditikrushna-nit/15min';
    window.open(calendarUrl, '_blank');
  };

  // Latest Blog logic
  const [latestPosts, setLatestPosts] = useState<BlogPostType[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);

  useEffect(() => {
    getAllPosts().then(posts => {
      if (posts && posts.length > 0) {
        setLatestPosts(posts.slice(0, 2));
      }
      setBlogLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      
      {/* Hero Section */}
      <main className="min-h-screen flex items-center justify-center px-2 sm:px-4 md:px-6 py-12 sm:py-16 md:py-24 pt-20 sm:pt-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start w-full">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in w-full">
            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 leading-tight break-words">
                DitiKrushna Giri
              </h1>
              
              <div className="text-lg sm:text-xl md:text-2xl font-medium text-green-600 dark:text-green-400">
                <a 
                  href="https://www.cyberdesk.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group relative inline-block hover:text-green-700 dark:hover:text-green-300 transition-colors"
                >
                  Senior Software Engineer @{' '}
                  <span className="relative">
                    CyberDesk GmbH
                    <span 
                      className="absolute -bottom-1 left-0 w-full h-0.5"
                      style={{
                        background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)',
                        backgroundSize: '300% 100%',
                        animation: 'gradient-shift 3s ease infinite'
                      }}
                    ></span>
                  </span>
                </a>
              </div>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
                Building secure, scalable cloud infrastructure and leading engineering teams 
                to turn ambitious ideas into reality. Passionate about cybersecurity, 
                distributed systems, and crafting elegant technical solutions.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <Link
                to="/about"
                className="group relative overflow-hidden inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-all duration-300 hover:scale-105 shadow-lg font-medium w-full sm:w-auto justify-center"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></span>
                <span className="relative z-10">Learn More</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform relative z-10" />
              </Link>
              
              <button 
                onClick={handleBookingRedirect}
                className="group relative overflow-hidden inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg hover:from-green-600 hover:to-blue-700 transition-all duration-300 hover:scale-105 shadow-lg font-medium w-full sm:w-auto justify-center"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-500 ease-in-out"></span>
                <Calendar className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300 relative z-10" />
                <span className="relative z-10">Book a Meeting</span>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm shadow-green-400/50"></div>
              </button>
            </div>
            
            {/* Stats with Company List */}
            <div className="pt-8 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">4+</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Years Experience</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">50+</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Projects Built</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">5+</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Teams Led</div>
                </div>
              </div>
              
              {/* Company List integrated here */}
              <CompanyList />

              {/* Education Badge - compact, inline style */}
              {/* <div className="flex justify-center mt-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 border border-blue-100 dark:border-blue-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                  <span className="text-lg">🎓</span>
                  <span className="font-semibold text-blue-700 dark:text-blue-300">Alma Mater</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">National Institute of Technology, Tiruchirappalli, India</span>
                </div>
              </div> */}

              {/* Latest Blog Section */}
              <div className="mt-3 animate-float" style={{ animationDelay: '0.2s' }}>
                <div className="bg-white/80 dark:bg-gray-800/80 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 relative overflow-hidden">
                  {/* Animated background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-blue-500/5 to-cyan-500/5 animate-gradient-shift"></div>
                  
                  <div className="flex items-center justify-center px-3 py-2 border-b border-gray-200 dark:border-gray-700 relative">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-green-500/50 to-transparent"></div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        Latest Posts
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      </span>
                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-green-500/50 to-transparent"></div>
                    </div>
                  </div>
                  
                  {blogLoading ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 text-center">Loading...</div>
                  ) : latestPosts.length > 0 ? (
                    <div className="flex divide-x divide-gray-200 dark:divide-gray-700">
                      {latestPosts.map((post, index) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.id}`}
                          className="w-1/2 group relative overflow-hidden"
                          style={{ 
                            animationDelay: `${index * 0.15}s`,
                            animation: 'slideIn 0.5s ease-out forwards'
                          }}
                        >
                          {/* Glowing border effect */}
                          <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-500/20 dark:group-hover:border-green-400/20 rounded-lg transition-all duration-500"></div>
                          
                          <div className="px-3 py-2 space-y-1 min-w-0 relative z-10 transform transition-transform duration-300 group-hover:translate-x-1">
                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                              <span className="whitespace-nowrap">{new Date(post.date).toLocaleDateString()}</span>
                              <span>·</span>
                              <span className="truncate">{post.author}</span>
                            </div>
                            <h4 className="text-xs font-bold text-green-600 dark:text-green-400 truncate group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors duration-300">
                              {post.title}
                            </h4>
                            <p className="text-[10px] text-gray-600 dark:text-gray-300 truncate">
                              {post.excerpt}
                            </p>
                          </div>
                          
                          {/* Hover gradient overlay */}
                          <div 
                            className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-blue-500/0 to-cyan-500/0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                            style={{
                              backgroundSize: '200% 100%',
                              animation: 'gradient-shift 3s ease infinite'
                            }}
                          ></div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 text-center">No blog posts found.</div>
                  )}
                </div>
              </div>
              {/* End Latest Blog Section */}

            </div>
          </div>
          
          {/* Right Content - GitHub Stats & Contributions */}
          <div className="relative animate-fade-in space-y-6 w-full" style={{ animationDelay: '0.2s' }}>
            {/* GitHub Stats Cards */}
            <GitHubStats />
            
            {/* GitHub Contributions Graph */}
            <GitHubContributions />
            
            {/* Floating elements */}
                            <div className="hidden sm:block absolute -top-4 -right-4 w-20 h-20 bg-green-500/20 rounded-lg rotate-12 animate-float"></div>
            <div className="hidden sm:block absolute -bottom-6 -left-6 w-16 h-16 bg-purple-500/20 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </main>
      
      {/* Quick Links Section - Reduced top padding */}
      <section className="py-6 px-2 sm:px-6 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Link
              to="/about"
              className="group p-4 sm:p-6 bg-white/80 dark:bg-gray-800/80 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                About Me
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                Learn about my journey, experience, and the technologies I work with.
              </p>
            </Link>
            
            <Link
              to="/projects"
              className="group p-4 sm:p-6 bg-white/80 dark:bg-gray-800/80 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Projects
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                Explore my weekend projects, side hustles, and passion projects.
              </p>
            </Link>
            
            <Link
              to="/contact"
              className="group p-4 sm:p-6 bg-white/80 dark:bg-gray-800/80 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Get In Touch
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                Let's discuss opportunities, collaborations, or just connect.
              </p>
            </Link>
          </div>
        </div>
      </section>

      <style>
        {`
          @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
            100% { transform: translateY(0px); }
          }
        `}
      </style>
    </div>
  );
};

export default Index;
